import { useAppSelector } from '@shared/hooks/useAppSelector.js';
import storage from 'local-storage-fallback';
import {
  type ChangeEvent,
  type MouseEvent,
  useCallback,
  useState,
} from 'react';

export function usePersistentState<
  T,
  E extends { value: string } = { value: string },
>(
  key: string,
  serialize: (value: T) => string,
  deserialize: (value: string | null) => T,
) {
  // Lazy: the stored value is only ever read as the component mounts, so a
  // costly `deserialize` mustn't run again on every render.
  const [value, setValue] = useState(() => deserialize(storage.getItem(key)));

  const cookiesEnabled = useAppSelector(
    (state) => state.cookieConsent.cookieConsentResult !== null,
  );

  const setState = useCallback(
    (value: T | ((value: T) => T)) => {
      if (typeof value === 'function') {
        const fn = value as (value: T) => T;

        setValue((prev) => {
          const next = fn(prev);

          if (cookiesEnabled) {
            storage.setItem(key, serialize(next));
          }

          return next;
        });
      } else {
        if (cookiesEnabled) {
          storage.setItem(key, serialize(value));
        }

        setValue(value);
      }
    },
    [key, cookiesEnabled, serialize],
  );

  const setFormState = useCallback(
    (evt: ChangeEvent<E> | MouseEvent<E>) => {
      const value = deserialize(evt.currentTarget.value);

      if (cookiesEnabled) {
        storage.setItem(key, serialize(value));
      }

      setValue(value);
    },
    [cookiesEnabled, key, deserialize, serialize],
  );

  return [value, setState, setFormState] as const;
}
